import { useState, useEffect, useCallback, useMemo, type KeyboardEvent, type FormEvent, type ChangeEvent } from 'react'
import { XMarkIcon, FolderPlusIcon } from '@heroicons/react/24/outline'
import { useCreateFolderMutation, type FolderResponse } from '@/hooks/useDatasourceFolders'
import { notify } from '@/utils/notify'

interface CreateFolderModalProps {
  isOpen: boolean
  onClose: () => void
  folders?: FolderResponse[]
  defaultParentFolderId?: string | null
}

export function CreateFolderModal({ isOpen, onClose, folders = [], defaultParentFolderId }: CreateFolderModalProps) {
  // Filter to only show root folders (max depth = 1)
  // This means we can only create folders inside root folders, not deeper
  const rootFolders = useMemo(() => {
    return folders.filter(folder => !folder.parentFolderId)
  }, [folders])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [parentFolderId, setParentFolderId] = useState<string | null>(null)
  const [error, setError] = useState('')

  const createFolderMutation = useCreateFolderMutation()

  useEffect(() => {
    if (isOpen) {
      setName('')
      setDescription('')
      setParentFolderId(defaultParentFolderId ?? null)
      setError('')
    }
  }, [isOpen, defaultParentFolderId])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  const handleFormSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()

      const trimmedName = name.trim()
      if (!trimmedName) {
        setError('Folder name is required')
        return
      }

      if (trimmedName.length > 100) {
        setError('Folder name must be less than 100 characters')
        return
      }

      setError('')

      void (async () => {
        try {
          await createFolderMutation.mutateAsync({
            name: trimmedName,
            description: description.trim() || undefined,
            parentFolderId: parentFolderId || undefined,
          })
          notify.success('Folder created')
          onClose()
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to create folder'
          if (errorMessage.includes('duplicate') || errorMessage.includes('exists')) {
            setError('A folder with this name already exists')
          } else {
            setError(errorMessage)
          }
        }
      })()
    },
    [name, description, parentFolderId, createFolderMutation, onClose]
  )

  const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
    setError('')
  }, [])

  const handleDescriptionChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value)
  }, [])

  const handleParentChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setParentFolderId(e.target.value || null)
  }, [])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FolderPlusIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create Folder</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleFormSubmit} className="p-4">
          {/* Name Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Folder Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={handleNameChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter folder name"
              maxLength={100}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-gray-400 dark:focus:border-gray-500"
              autoFocus
            />
            {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
          </div>

          {/* Description Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter folder description"
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:border-gray-400 dark:focus:border-gray-500 resize-none"
            />
          </div>

          {/* Parent Folder Selector - Only show root folders (max depth = 1) */}
          {rootFolders.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Parent Folder <span className="text-gray-400">(optional)</span>
              </label>
              <select
                value={parentFolderId || ''}
                onChange={handleParentChange}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-gray-400 dark:focus:border-gray-500"
              >
                <option value="">Root level (no parent)</option>
                {rootFolders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Nest this folder inside a root folder (max 1 level of nesting)
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={createFolderMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createFolderMutation.isPending || !name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {createFolderMutation.isPending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Folder'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
